/**
 * Experience-Weighted Piece Selector
 *
 * UCB1-based selection algorithm that balances exploitation (pick known winners)
 * with exploration (try rarely-used pieces). Each AI model develops unique
 * preferences based on its game history.
 *
 * Algorithm: Modified UCB1 (Upper Confidence Bound)
 *   score = winRate + C * sqrt(ln(totalGames) / pieceGames)
 *
 * The exploration constant C decays as more games are played:
 *   C = max(MIN_C, BASE_C / sqrt(totalGames))
 *
 * This means:
 * - Early (0-10 games): High exploration, near-random selection
 * - Mid (10-50 games): Balanced, emerging preferences
 * - Late (50+ games): Strong preferences, occasional exploration
 * - Mature (200+ games): Well-defined personality, rare exploration
 */

import { PieceType } from '../../zodiacEngine/types';
import { AIModel } from '../../zodiacEngine/AI/Core/AIVariants';
import { AIModelPieceStatsDoc, getSynergyKey, getMatchupKey, getAdjacentMatchupKey } from './PieceStatsTypes';
import { PieceStatsCache } from './PieceStatsCache';
import {
  getGeneralLaningQuality,
  getAllyAdjacencyBonus,
  getColumnPreference,
  hasAllyAdjacencyPrefs,
  getLaningSameCol,
  getLaningAdjacentCol,
} from './LaningHeuristics';

// === Tuning Constants ===

/** Minimum games before stats influence selection (cold start threshold) */
const MIN_GAMES_FOR_STATS = 5;

/** Base exploration constant for UCB1 (higher = more exploration) */
const KING_EXPLORE_BASE = 2.0;
const KING_EXPLORE_MIN = 0.1;

const PIECE_EXPLORE_BASE = 1.5;
const PIECE_EXPLORE_MIN = 0.05;

/** Weight multipliers for the piece score components */
const SYNERGY_WEIGHT = 0.4;
const SURVIVAL_WEIGHT = 0.1;
const COLUMN_WEIGHT = 0.15;
const MATCHUP_WEIGHT = 0.1;
const LANING_HEURISTIC_WEIGHT = 0.15;
const OPPONENT_MATCHUP_WEIGHT = 0.15;

/** Score assigned to untried pieces/kings (high to encourage exploration) */
const UNTRIED_SCORE = 10;

/** Number of top candidates to sample from (prevents pure argmax) */
const KING_TOP_N = 3;

export class ExperienceWeightedSelector {

  // ─── King Selection ───────────────────────────────────────────

  /**
   * Select a king weighted by experience.
   *
   * @param availableKings - Valid king types for this bot (from preferredKings or all types)
   * @param stats - The AI model's learned stats (null = cold start)
   * @param bannedPieces - Pieces this bot cannot use
   * @returns Selected king PieceType
   */
  static selectKing(
    availableKings: PieceType[],
    stats: AIModelPieceStatsDoc | null,
    bannedPieces?: PieceType[]
  ): PieceType {
    const banned = new Set(bannedPieces ?? []);
    const validKings = availableKings.filter(
      k => k !== PieceType.Virus && !banned.has(k)
    );

    if (validKings.length === 0) {
      // Fallback: any non-Virus, non-banned piece
      const allTypes = Object.values(PieceType).filter(
        pt => pt !== PieceType.Virus && !banned.has(pt)
      );
      return allTypes[Math.floor(Math.random() * allTypes.length)];
    }

    if (validKings.length === 1) return validKings[0];

    // Cold start: uniform random
    if (!stats || stats.totalGames < MIN_GAMES_FOR_STATS) {
      return validKings[Math.floor(Math.random() * validKings.length)];
    }

    const totalGames = stats.totalGames;
    const C = Math.max(KING_EXPLORE_MIN, KING_EXPLORE_BASE / Math.sqrt(totalGames));

    const scores = validKings.map(king => {
      const ps = stats.pieceStats[king];
      if (!ps || ps.gamesAsKing === 0) {
        return { king, score: UNTRIED_SCORE };
      }

      const winRate = ps.winsAsKing / ps.gamesAsKing;
      const exploration = C * Math.sqrt(Math.log(totalGames) / ps.gamesAsKing);
      return { king, score: winRate + exploration };
    });

    // Sort descending by score
    scores.sort((a, b) => b.score - a.score);

    // Weighted random from top N candidates (prevents full determinism)
    const topN = scores.slice(0, Math.min(KING_TOP_N, scores.length));
    return this.weightedRandomPick(topN.map(s => ({ item: s.king, weight: s.score })));
  }

  // ─── Piece Selection ──────────────────────────────────────────

  /**
   * Select a piece for the army, considering already-selected pieces for synergy.
   *
   * Combines four signals:
   * 1. Base win rate in army (UCB1)
   * 2. Synergy bonus with already-selected pieces
   * 3. Survival rate bonus
   * 4. Column-specific performance (if column is known)
   *
   * @param availablePieces - Pieces eligible for selection
   * @param alreadySelected - Pieces already in the army (for synergy)
   * @param stats - The AI model's learned stats (null = cold start)
   * @param bannedPieces - Pieces this bot cannot use
   * @param targetColumn - Column where the piece will be placed (for column stats)
   * @returns Selected PieceType
   */
  static selectPiece(
    availablePieces: PieceType[],
    alreadySelected: PieceType[],
    stats: AIModelPieceStatsDoc | null,
    bannedPieces?: PieceType[],
    targetColumn?: number,
    opponentPieces?: PieceType[]
  ): PieceType {
    const banned = new Set(bannedPieces ?? []);
    const selected = new Set(alreadySelected);
    const eligible = availablePieces.filter(p => !banned.has(p) && !selected.has(p));

    if (eligible.length === 0) {
      // Should not happen, but safety fallback
      return availablePieces[Math.floor(Math.random() * availablePieces.length)];
    }

    if (eligible.length === 1) return eligible[0];

    // Cold start: uniform random
    if (!stats || stats.totalGames < MIN_GAMES_FOR_STATS) {
      return eligible[Math.floor(Math.random() * eligible.length)];
    }

    const totalGames = stats.totalGames;
    const C = Math.max(PIECE_EXPLORE_MIN, PIECE_EXPLORE_BASE / Math.sqrt(totalGames));

    const scores = eligible.map(piece => {
      const ps = stats.pieceStats[piece];
      let score = 0;

      // 1. Base army win rate (UCB1)
      if (ps && ps.gamesInArmy > 0) {
        const winRate = ps.winsInArmy / ps.gamesInArmy;
        const exploration = C * Math.sqrt(Math.log(totalGames) / ps.gamesInArmy);
        score = winRate + exploration;
      } else {
        // Untried: high exploration bonus
        score = 0.5 + C * UNTRIED_SCORE;
      }

      // 2. Synergy bonus with already-selected pieces
      if (alreadySelected.length > 0) {
        let synergyDelta = 0;
        let synergyCount = 0;

        for (const partner of alreadySelected) {
          const key = getSynergyKey(piece, partner);
          const syn = stats.synergyStats[key];
          if (syn && syn.games >= 3) {
            const synergyRate = syn.wins / syn.games;
            const baseRate = ps ? ps.winsInArmy / Math.max(1, ps.gamesInArmy) : 0.5;
            synergyDelta += synergyRate - baseRate;
            synergyCount++;
          }
        }

        if (synergyCount > 0) {
          score += (synergyDelta / synergyCount) * SYNERGY_WEIGHT;
        }
      }

      // 3. Survival bonus
      if (ps && ps.gamesInArmy > 0) {
        const survivalRate = ps.survivalCount / ps.gamesInArmy;
        score += survivalRate * SURVIVAL_WEIGHT;
      }

      // 4. Column performance
      if (targetColumn !== undefined && ps?.columnStats[targetColumn]) {
        const colStats = ps.columnStats[targetColumn];
        if (colStats.games >= 3) {
          const colWinRate = colStats.wins / colStats.games;
          score += (colWinRate - 0.5) * COLUMN_WEIGHT;
        }
      }

      // 5. Matchup resilience
      // When opponent army is known: filter to specific matchup keys (e.g., "Bear>Crane")
      // When unknown: aggregate all matchups (original behavior)
      let matchupGames = 0;
      if (stats.matchupStats) {
        let matchupWins = 0;
        if (opponentPieces && opponentPieces.length > 0) {
          // Opponent-aware: check specific matchups against known opponent pieces
          for (const opp of opponentPieces) {
            const sameKey = getMatchupKey(piece, opp);
            const adjKey = getAdjacentMatchupKey(piece, opp);
            const same = stats.matchupStats[sameKey];
            const adj = stats.matchupStats[adjKey];
            if (same && same.games >= 2) {
              matchupWins += same.wins;
              matchupGames += same.games;
            }
            if (adj && adj.games >= 2) {
              matchupWins += adj.wins;
              matchupGames += adj.games;
            }
          }
        } else {
          // No opponent info: aggregate all matchups
          for (const [key, mStats] of Object.entries(stats.matchupStats)) {
            if (key.startsWith(`${piece}>`) && mStats.games >= 3) {
              matchupWins += mStats.wins;
              matchupGames += mStats.games;
            }
          }
        }
        if (matchupGames > 0) {
          const matchupWinRate = matchupWins / matchupGames;
          score += (matchupWinRate - 0.5) * MATCHUP_WEIGHT;
        }
      }

      // 6. Static laning quality (human knowledge baseline)
      // Blends with learned matchup data: as learned games increase, static influence decreases
      // but never drops below 20% — human knowledge is always partially trusted
      {
        const staticScore = getGeneralLaningQuality(piece);
        const blendWeight = matchupGames >= 30
          ? 0.2
          : (1.0 - matchupGames / 30 * 0.8);
        score += staticScore * LANING_HEURISTIC_WEIGHT * blendWeight;
      }

      // 7. Opponent-specific laning heuristic (when opponent army is known)
      // Uses static same-lane and adjacent-lane scores against each known opponent piece
      if (opponentPieces && opponentPieces.length > 0) {
        let laningSum = 0;
        for (const opp of opponentPieces) {
          laningSum += getLaningSameCol(piece, opp);
          laningSum += getLaningAdjacentCol(piece, opp) * 0.5; // Adjacent less impactful
        }
        const avgLaning = laningSum / (opponentPieces.length * 1.5); // Normalize
        score += avgLaning * OPPONENT_MATCHUP_WEIGHT;
      }

      return { piece, score };
    });

    // Weighted random from all eligible (shift scores to positive)
    const minScore = Math.min(...scores.map(s => s.score));
    const items = scores.map(s => ({
      item: s.piece,
      weight: Math.max(0.01, s.score - minScore + 0.1),
    }));

    return this.weightedRandomPick(items);
  }

  // ─── Column Selection ─────────────────────────────────────────

  /**
   * Select the best column for a piece based on historical performance,
   * ally adjacency bonuses, and column preference heuristics.
   *
   * @param piece - The piece type to place
   * @param emptyColumns - Available empty column indices
   * @param stats - The AI model's learned stats
   * @param allyPlacements - Already-placed allies with their column assignments
   * @returns Selected column index
   */
  static selectColumn(
    piece: PieceType,
    emptyColumns: number[],
    stats: AIModelPieceStatsDoc | null,
    allyPlacements?: Map<PieceType, number>
  ): number {
    if (emptyColumns.length <= 1) return emptyColumns[0];

    const scores = emptyColumns.map(col => {
      let weight = 1.0; // Base weight

      // 1. Historical column performance
      if (stats && stats.totalGames >= MIN_GAMES_FOR_STATS) {
        const ps = stats.pieceStats[piece];
        if (ps) {
          const colStats = ps.columnStats[col];
          if (colStats && colStats.games >= 2) {
            const winRate = colStats.wins / colStats.games;
            weight = Math.max(0.1, winRate + 0.3);
          }
        }
      }

      // 2. Ally adjacency bonus (from static laning heuristics)
      if (allyPlacements) {
        for (const [ally, allyCol] of allyPlacements) {
          if (Math.abs(col - allyCol) === 1) {
            weight += getAllyAdjacencyBonus(piece, ally) * 0.02;
          }
        }
      }

      // 3. Column preference (inherent affinity)
      weight += getColumnPreference(piece, col) * 0.01;

      return { col, weight: Math.max(0.01, weight) };
    });

    return this.weightedRandomPick(scores.map(s => ({ item: s.col, weight: s.weight })));
  }

  // ─── Full Army Selection ────────────────────────────────────────

  /**
   * Select a complete army (king + 4 pieces + column assignments) for an AI model.
   * Used by ArenaService for experience-weighted piece placement.
   *
   * @param model - AI model to select for
   * @param eligiblePieces - Pieces eligible for army selection (excludes Virus in Arena)
   * @param bannedPieces - Pieces this model cannot use
   * @returns King, army pieces, and column assignments for all 5 pieces
   */
  static async selectFullArmyForAI(
    model: AIModel,
    eligiblePieces: PieceType[],
    bannedPieces?: PieceType[],
    opponentPieces?: PieceType[]
  ): Promise<{ king: PieceType; pieces: PieceType[]; columns: number[] }> {
    const cache = PieceStatsCache.getInstance();
    const stats = await cache.getStats(model);

    // Eligible kings: same as eligible pieces minus ineligible king types
    const ineligibleKings = new Set([PieceType.Virus, PieceType.Octopus, PieceType.Penguin]);
    const eligibleKings = eligiblePieces.filter(p => !ineligibleKings.has(p));

    // 1. Select king
    const king = this.selectKing(eligibleKings, stats, bannedPieces);

    // 2. Select 4 army pieces sequentially (synergy-aware)
    const army: PieceType[] = [];
    for (let i = 0; i < 4; i++) {
      const available = eligiblePieces.filter(
        p => p !== king && !army.includes(p)
      );
      const pick = this.selectPiece(available, [king, ...army], stats, bannedPieces, undefined, opponentPieces);
      army.push(pick);
    }

    // 3. Assign columns for all 5 pieces (king + 4 army)
    // Sort: pieces with ally adjacency preferences first (e.g., Monkey/Tree, Wolf/Rhino)
    // so critical pairs get adjacent columns before other pieces fill gaps
    const allPieces = [king, ...army];
    const pieceIndices = allPieces.map((p, i) => i);
    pieceIndices.sort((a, b) => {
      const aHasPrefs = hasAllyAdjacencyPrefs(allPieces[a]) ? 0 : 1;
      const bHasPrefs = hasAllyAdjacencyPrefs(allPieces[b]) ? 0 : 1;
      return aHasPrefs - bHasPrefs;
    });

    const columns: number[] = new Array(allPieces.length);
    const remainingCols = [0, 1, 2, 3, 4];
    const allyPlacements = new Map<PieceType, number>();

    for (const idx of pieceIndices) {
      const piece = allPieces[idx];
      const col = this.selectColumn(piece, remainingCols, stats, allyPlacements);
      columns[idx] = col;
      allyPlacements.set(piece, col);
      remainingCols.splice(remainingCols.indexOf(col), 1);
    }

    return { king, pieces: army, columns };
  }

  /**
   * Select army pieces + columns for a pre-selected king.
   * Used when an AI has its own king picker but no piece picker.
   */
  static async selectArmyPiecesForAI(
    model: AIModel,
    king: PieceType,
    eligiblePieces: PieceType[],
    bannedPieces?: PieceType[],
    opponentPieces?: PieceType[]
  ): Promise<{ pieces: PieceType[]; columns: number[] }> {
    const cache = PieceStatsCache.getInstance();
    const stats = await cache.getStats(model);

    const army: PieceType[] = [];
    for (let i = 0; i < 4; i++) {
      const available = eligiblePieces.filter(
        p => p !== king && !army.includes(p)
      );
      const pick = this.selectPiece(available, [king, ...army], stats, bannedPieces, undefined, opponentPieces);
      army.push(pick);
    }

    const allPieces = [king, ...army];
    const pieceIndices = allPieces.map((_, i) => i);
    pieceIndices.sort((a, b) => {
      const aHasPrefs = hasAllyAdjacencyPrefs(allPieces[a]) ? 0 : 1;
      const bHasPrefs = hasAllyAdjacencyPrefs(allPieces[b]) ? 0 : 1;
      return aHasPrefs - bHasPrefs;
    });

    const columns: number[] = new Array(allPieces.length);
    const remainingCols = [0, 1, 2, 3, 4];
    const allyPlacements = new Map<PieceType, number>();

    for (const idx of pieceIndices) {
      const piece = allPieces[idx];
      const col = this.selectColumn(piece, remainingCols, stats, allyPlacements);
      columns[idx] = col;
      allyPlacements.set(piece, col);
      remainingCols.splice(remainingCols.indexOf(col), 1);
    }

    return { pieces: army, columns };
  }

  // ─── Convenience Methods ──────────────────────────────────────

  /**
   * Convenience: Select king for an AI model, fetching stats from cache.
   */
  static async selectKingForAI(
    availableKings: PieceType[],
    aiModel: AIModel,
    bannedPieces?: PieceType[]
  ): Promise<PieceType> {
    try {
      const cache = PieceStatsCache.getInstance();
      const stats = await cache.getStats(aiModel);
      return this.selectKing(availableKings, stats, bannedPieces);
    } catch {
      // Fallback to random on any error
      const valid = availableKings.filter(k => k !== PieceType.Virus);
      return valid[Math.floor(Math.random() * valid.length)];
    }
  }

  /**
   * Convenience: Select piece for an AI model, fetching stats from cache.
   */
  static async selectPieceForAI(
    availablePieces: PieceType[],
    alreadySelected: PieceType[],
    aiModel: AIModel,
    bannedPieces?: PieceType[],
    targetColumn?: number
  ): Promise<PieceType> {
    try {
      const cache = PieceStatsCache.getInstance();
      const stats = await cache.getStats(aiModel);
      return this.selectPiece(availablePieces, alreadySelected, stats, bannedPieces, targetColumn);
    } catch {
      // Fallback to random on any error
      const eligible = availablePieces.filter(
        p => !alreadySelected.includes(p) && !(bannedPieces ?? []).includes(p)
      );
      return eligible[Math.floor(Math.random() * eligible.length)];
    }
  }

  // ─── Utility ──────────────────────────────────────────────────

  /**
   * Weighted random selection from a list of items.
   * Each item has a weight >= 0. Higher weight = higher probability of being picked.
   */
  private static weightedRandomPick<T>(items: { item: T; weight: number }[]): T {
    const totalWeight = items.reduce((sum, i) => sum + i.weight, 0);
    if (totalWeight <= 0) {
      return items[Math.floor(Math.random() * items.length)].item;
    }

    let rand = Math.random() * totalWeight;
    for (const entry of items) {
      rand -= entry.weight;
      if (rand <= 0) return entry.item;
    }

    return items[items.length - 1].item;
  }
}
